import Centrifuge from 'centrifuge';
import { pl } from 'date-fns/locale';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Bonus,
  Freespin,
  Jackpot,
  Lootbox,
  PlayerJackpotWin,
  PlayerTransaction,
} from '@/api/common';
import { RecentGame } from '@/api/games';
import { TopPlayer } from '@/api/tournaments';
import { camelize } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { checkIfLootboxActiveById } from '@/helpers/realTimeService';
import { useCommonStore, useModalsStore } from '@/store';

export type WSResponse<R> = {
  uid: string;
  channel: string;
  data: R;
};

// TODO: refactor this
export const useRealTimeService = () => {
  const {
    playerSettings,
    setActiveJackpot,
    addPlayerJackpotWin,
    fetchJackpotWinnersList,
    fetchLootboxes,
    updateLootboxes,
    updateBonuses,
    fetchBonuses,
    updateFreespins,
    fetchFreespins,
    updatePlayerPayments,
    updateRecentGames,
    fetchRecentGamesIds,
    fetchUserTournamentStatus,
    updateUserTournamentStatus,
    user,
    withoutTagsLootboxes,
    activeLootboxId,
  } = useCommonStore(
    useShallow((state) => ({
      playerSettings: state.playerSettings,
      setActiveJackpot: state.setActiveJackpot,
      addPlayerJackpotWin: state.addPlayerJackpotWin,
      fetchJackpotWinnersList: state.fetchJackpotWinnersList,
      fetchLootboxes: state.fetchLootboxes,
      updateLootboxes: state.updateLootboxes,
      updateBonuses: state.updateBonuses,
      fetchBonuses: state.fetchBonuses,
      updateFreespins: state.updateFreespins,
      fetchFreespins: state.fetchFreespins,
      updatePlayerPayments: state.updatePlayerPayments,
      updateRecentGames: state.updateRecentGames,
      fetchRecentGamesIds: state.fetchRecentGamesIds,
      fetchUserTournamentStatus: state.fetchUserTournamentStatus,
      updateUserTournamentStatus: state.updateUserTournamentStatus,
      user: state.user,
      withoutTagsLootboxes: state.withoutTagsLootboxes,
      activeLootboxId: state.activeLootboxId,
    }))
  );

  const open = useModalsStore.use.open();

  const centrifuge = useMemo(() => {
    if (!playerSettings?.cent) return null;

    return new Centrifuge({
      url: playerSettings.cent.url + '/connection/websocket',
      user: playerSettings.cent.user,
      timestamp: playerSettings.cent.timestamp,
      token: playerSettings.cent.token,
    });
  }, [playerSettings?.cent]);

  useEffect(() => {
    if (!centrifuge) return;

    centrifuge.subscribe(
      `public:jackpots_changes`,
      (context: WSResponse<Jackpot>) => {
        useCommonStore.setState({
          activeJackpot: camelize(context.data) as Jackpot,
        });
      }
    );

    if (playerSettings?.cent.user) {
      centrifuge.subscribe(
        `jackpot_win_award`,
        (context: WSResponse<PlayerJackpotWin>) => {
          addPlayerJackpotWin(camelize(context.data) as PlayerJackpotWin);
        }
      );

      // ******  subscribe to  analytics channel
      centrifuge.subscribe(
        `$analytics#${playerSettings.cent.user}`,
        (context: WSResponse<any>) => {
          // TODO: add correct handler after SS fixes
          console.log('######## $analytics#', context);
        }
      );
      // ******

      centrifuge.subscribe(
        `bonuses_changes#${playerSettings.cent.user}`,
        (context: WSResponse<Bonus>) => {
          updateBonuses(camelize(context.data) as Bonus);
        }
      );

      centrifuge.subscribe(
        `freespins_changes#${playerSettings.cent.user}`,
        (context: WSResponse<Freespin>) => {
          updateFreespins(camelize(context.data) as Freespin);
        }
      );

      centrifuge.subscribe(
        `lootboxes_changes#${playerSettings.cent.user}`,
        (context: WSResponse<Lootbox>) => {
          updateLootboxes(camelize(context.data) as Lootbox);
        }
      );

      centrifuge.subscribe(
        `payments_changes#${playerSettings.cent.user}`,
        (context: WSResponse<PlayerTransaction>) => {
          updatePlayerPayments(camelize(context.data) as PlayerTransaction);
        }
      );

      centrifuge.subscribe(
        `played_games#${playerSettings.cent.user}`,
        (context: WSResponse<RecentGame>) => {
          updateRecentGames(camelize(context.data) as RecentGame);
        }
      );

      centrifuge.subscribe(
        `tournaments_statuses#${playerSettings.cent.user}`,
        (context: WSResponse<TopPlayer[]>) => {
          updateUserTournamentStatus(camelize(context.data) as TopPlayer);
        }
      );

      centrifuge.subscribe(
        `referral_system_notifications#${user?.id}`,
        (context: WSResponse<{ text: string }>) => {
          if (context.data.text) {
            open(Modals.RefBonusNotification);
          }
        }
      );
    }

    centrifuge.connect();
    console.log('RealTimeService connected (Centrifuge) - ', centrifuge);

    return () => {
      // TODO: TBD just for test
      centrifuge.removeAllListeners();
      centrifuge.disconnect();
    };
  }, [
    centrifuge,
    playerSettings?.cent,
    addPlayerJackpotWin,
    updateBonuses,
    updateFreespins,
    updateLootboxes,
    updatePlayerPayments,
    updateRecentGames,
    updateUserTournamentStatus,
  ]);

  useEffect(() => {
    setActiveJackpot();
    fetchJackpotWinnersList();
  }, [setActiveJackpot, fetchJackpotWinnersList]);

  useEffect(() => {
    if (!user?.id) return;

    fetchBonuses();
    fetchFreespins();
    fetchRecentGamesIds();
    fetchLootboxes();
    fetchUserTournamentStatus();
  }, [
    fetchLootboxes,
    fetchBonuses,
    fetchFreespins,
    fetchRecentGamesIds,
    fetchUserTournamentStatus,
    user?.id,
  ]);

  useEffect(() => {
    const lastLootbox = withoutTagsLootboxes
      .filter((lootbox) => lootbox.live)
      .pop();

    if (lastLootbox) {
      const isActive = checkIfLootboxActiveById(
        withoutTagsLootboxes,
        lastLootbox.id
      );

      !activeLootboxId &&
        isActive &&
        useCommonStore.setState({ activeLootboxId: lastLootbox.id });
    }
  }, [activeLootboxId, withoutTagsLootboxes]);
};
